import { useRef, useState } from 'react'
import { Button, Drawer, Form, FormInstance, Input, notification } from 'antd'

import type { PageCreate } from '../../../../services/bb-api/oplevalt/cms/page'
import { txt } from '../../../../locales/i18n'

export type CreatePageDrawerProps = {
	visible: boolean
	onClose: () => void
	onCreate: (page: any) => Promise<void>
}

export const CreatePageDrawer = (props: CreatePageDrawerProps) => {
	const [isCreating, setIsCreating] = useState(false)
	const formRef = useRef<FormInstance>(null)

	const createPage = async (page: PageCreate) => {
		const form = formRef.current
		if (form === null) return

		const [urlError = null] = form.getFieldError('url')
		if (urlError !== null) return notification.error({ message: urlError })

		setIsCreating(true)

		try {
			await props.onCreate(page)
			notification.success({ message: txt('notification.create successful') })
		} catch (error) {
			if (error instanceof Error && error.message === 'url already exists') {
				const errorMessage = txt('cms.notifications.urlExists')
				form.setFields([{ name: 'url', errors: [errorMessage] }])
			}

			notification.error({ message: txt('notification.create failed') })
		}

		setIsCreating(false)
	}

	const handleValuesChange = (values: any) => {
		const form = formRef.current
		if (form === null) return

		const [[key, value]] = Object.entries(values)

		if (key === 'title') {
			form.setFields([{ name: key, errors: [] }])

			const maxTitleLength = 60
			const titleLength = (value as string).length
			if (titleLength < maxTitleLength) return

			const errorMessage = txt('cms.notifications.titleLength', {
				maxTitleLength,
			})

			form.setFields([{ name: key, errors: [errorMessage] }])
		}

		if (key === 'metaDescription') {
			form.setFields([{ name: key, errors: [] }])

			const maxMetaDescriptionLength = 160
			const metaDescriptionLength = (value as string).length
			if (metaDescriptionLength < maxMetaDescriptionLength) return

			const errorMessage = txt('cms.notifications.metaDescriptionLength', {
				maxMetaDescriptionLength,
			})

			form.setFields([{ name: key, errors: [errorMessage] }])
		}

		if (key === 'url') {
			form.setFields([{ name: key, errors: [] }])
			const urlPattern = /^\w[/\w-]*$/gi
			if (urlPattern.test(value as string)) return

			const errorMessage = txt('cms.notifications.urlFormat')
			form.setFields([{ name: key, errors: [errorMessage] }])
		}
	}

	return (
		<Drawer title={txt('cms.createPage')} visible={props.visible} onClose={props.onClose} placement="left" width={420}>
			<Form
				ref={formRef}
				onFinish={createPage}
				onValuesChange={handleValuesChange}
				initialValues={{ region: 'dk' }}
			>
				<label>{txt('cms.title')}</label>
				<Form.Item name="title">
					<Input.TextArea autoSize showCount required />
				</Form.Item>

				<label>{txt('cms.url')}</label>
				<Form.Item name="url">
					<Input required />
				</Form.Item>

				<label>{txt('cms.metaDescription')}</label>
				<Form.Item name="metaDescription">
					<Input.TextArea autoSize showCount required />
				</Form.Item>

				<Form.Item>
					<Button type="primary" htmlType="submit" loading={isCreating}>
						{txt('cms.createPage')}
					</Button>
				</Form.Item>
			</Form>
		</Drawer>
	)
}
